import React, { useState } from 'react';
import { 
  Layout,
  Typography, 
  Card, 
  Tabs,
  List,
  Button,
  Avatar,
  Space,
  Tag,
  Badge,
  Empty,
  message,
  Rate,
  Modal,
  Divider
} from 'antd';
import { 
  CalendarOutlined,
  ClockCircleOutlined,
  CarOutlined,
  CoffeeOutlined,
  WifiOutlined,
  KeyOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  ShoppingOutlined,
  CustomerServiceOutlined,
  UserOutlined,
  HomeOutlined,
  IdcardOutlined,
  SearchOutlined,
  CommentOutlined,
  StarOutlined,
  SwapOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Header, Content, Footer } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

interface ServiceItem {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  status?: 'available' | 'processing' | 'completed';
  time?: string;
  tags?: string[];
}

// 预订前服务
const preBookingServices: ServiceItem[] = [
  {
    id: 'parking',
    title: '停车预约',
    description: '提前预约车位，享受便捷停车服务',
    icon: <CarOutlined />,
    tags: ['免费预约', '限量']
  },
  {
    id: 'breakfast',
    title: '早餐预订',
    description: '提前预订早餐，享受优惠价格',
    icon: <CoffeeOutlined />,
    tags: ['优惠', '自助']
  },
  {
    id: 'pickup',
    title: '接送服务',
    description: '机场/高铁站专业接送服务',
    icon: <CarOutlined />,
    tags: ['收费', '预约']
  }
];

// 入住中服务
const inStayServices: ServiceItem[] = [
  {
    id: 'wifi',
    title: 'WiFi连接',
    description: '高速无线网络服务',
    icon: <WifiOutlined />,
    status: 'available'
  },
  {
    id: 'roomService',
    title: '客房服务',
    description: '24小时管家服务',
    icon: <CustomerServiceOutlined />,
    status: 'available'
  },
  {
    id: 'dining',
    title: '餐饮服务',
    description: '客房送餐及餐厅预订',
    icon: <CoffeeOutlined />,
    status: 'available'
  },
  {
    id: 'cleaning',
    title: '客房清洁',
    description: '专业清洁消毒服务',
    icon: <CheckCircleOutlined />,
    status: 'processing',
    time: '预计30分钟内完成'
  },
  {
    id: 'maintenance',
    title: '设施报修',
    description: '24小时设备维修服务',
    icon: <KeyOutlined />,
    status: 'available'
  }
];

// 离店后服务
const postStayServices: ServiceItem[] = [
  {
    id: 'invoice',
    title: '发票开具',
    description: '电子发票及纸质发票服务',
    icon: <ShoppingOutlined />,
    status: 'available'
  },
  {
    id: 'lostFound',
    title: '失物招领',
    description: '遗失物品查找与寄送服务',
    icon: <SearchOutlined />,
    status: 'available'
  },
  {
    id: 'feedback',
    title: '住宿评价',
    description: '分享您的住宿体验',
    icon: <CommentOutlined />,
    status: 'available'
  }
];

// 当前订单
const currentBooking = {
  roomNo: '8088',
  checkIn: '2024-03-20 14:00',
  checkOut: '2024-03-22 12:00',
  status: 'in-stay' // pre-stay, in-stay, post-stay
};

// 简化管家信息接口
interface Butler {
  id: string;
  name: string;
  avatar: string;
  rating: number;
  status: 'online' | 'offline';
}

// 简化管家数据
const currentButler: Butler = {
  id: '1',
  name: '张丽',
  avatar: '',
  rating: 4.8,
  status: 'online'
};

const HotelServicePage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(
    currentBooking.status === 'in-stay' ? 'in-stay' : 
    currentBooking.status === 'post-stay' ? 'post-stay' : 
    'pre-stay'
  );
  const [rateModalVisible, setRateModalVisible] = useState(false);
  const [rating, setRating] = useState(5);
  const [rateComment, setRateComment] = useState('');

  const handleService = (service: ServiceItem) => {
    message.info(`正在为您处理${service.title}服务，请稍候...`);
    // 这里添加具体服务处理逻辑
  };

  const renderServiceList = (services: ServiceItem[]) => (
    <List
      itemLayout="horizontal"
      dataSource={services}
      renderItem={item => (
        <List.Item>
          <Card 
            style={{ width: '100%' }}
            hoverable
            onClick={() => handleService(item)}
          >
            <Space align="start" style={{ width: '100%' }}>
              <Avatar 
                icon={item.icon} 
                style={{ 
                  backgroundColor: '#529C8A',
                  color: '#fff'
                }} 
              />
              <Space direction="vertical" style={{ flex: 1 }}>
                <Space>
                  <Text strong>{item.title}</Text>
                  {item.status === 'processing' && (
                    <Badge status="processing" text="处理中" />
                  )}
                </Space>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {item.description}
                </Text>
                {item.time && (
                  <div style={{ fontSize: 12, color: '#529C8A' }}>
                    <ClockCircleOutlined /> {item.time}
                  </div>
                )}
                {item.tags && (
                  <Space size={[0, 4]} wrap>
                    {item.tags.map(tag => (
                      <Tag key={tag} color="blue">{tag}</Tag>
                    ))}
                  </Space>
                )}
              </Space>
            </Space>
          </Card>
        </List.Item>
      )}
    />
  );

  const renderCurrentBooking = () => (
    <Card style={{ margin: '16px', background: '#f6ffed', borderLeft: '4px solid #52c41a' }}>
      <Space direction="vertical" size="small">
        <Space>
          <HomeOutlined />
          <Text strong>当前房间: {currentBooking.roomNo}</Text>
        </Space>
        <Space>
          <CalendarOutlined />
          <Text type="secondary">入住: {currentBooking.checkIn}</Text>
        </Space>
        <Space>
          <CalendarOutlined />
          <Text type="secondary">离店: {currentBooking.checkOut}</Text>
        </Space>
      </Space>
    </Card>
  );

  const renderButlerCard = () => (
    <Card style={{ margin: '0 16px 16px' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Space align="start" style={{ width: '100%' }}>
          <Badge dot status={currentButler.status === 'online' ? 'success' : 'default'}>
            <Avatar 
              size={64} 
              src={currentButler.avatar} 
              icon={<UserOutlined />}
              style={{ backgroundColor: '#529C8A' }}
            />
          </Badge>
          <Space direction="vertical">
            <Space align="center">
              <Text strong style={{ fontSize: 16 }}>{currentButler.name}</Text>
              <Tag color="gold">金牌管家</Tag>
            </Space>
            <Space>
              <Rate disabled defaultValue={currentButler.rating} style={{ fontSize: 12 }} />
              <Text type="secondary">{currentButler.rating}</Text>
            </Space>
          </Space>
        </Space>

        <Space wrap style={{ width: '100%', justifyContent: 'space-around' }}>
          <Button 
            type="primary" 
            icon={<PhoneOutlined />}
            onClick={() => message.success('正在为您接通管家电话...')}
          >
            电话联系
          </Button>
          <Button 
            icon={<StarOutlined />}
            onClick={() => setRateModalVisible(true)}
          >
            服务评价
          </Button>
          <Button 
            icon={<SwapOutlined />}
            onClick={() => message.info('更换管家申请已提交，工作人员将尽快处理')}
          >
            更换管家
          </Button>
        </Space>
      </Space>
    </Card>
  );

  const tabItems = [
    {
      key: 'pre-stay',
      label: '预订服务',
      children: renderServiceList(preBookingServices)
    },
    {
      key: 'in-stay',
      label: '入住服务',
      children: renderServiceList(inStayServices)
    },
    {
      key: 'post-stay',
      label: '离店服务',
      children: renderServiceList(postStayServices)
    }
  ];

  return (
    <div style={{ 
      maxWidth: 500, 
      margin: '0 auto', 
      minHeight: '100vh',
      background: '#f5f5f5',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{ 
        background: '#fff',
        padding: '12px 16px',
        borderBottom: '1px solid #f0f0f0'
      }}>
        <Title level={4} style={{ margin: 0 }}>酒店服务</Title>
      </div>

      <div style={{ 
        flex: 1, 
        overflow: 'auto', 
        WebkitOverflowScrolling: 'touch',
        paddingBottom: 60 
      }}>
        {renderCurrentBooking()}
        {renderButlerCard()}
        
        <Card style={{ margin: '0 16px 16px' }}>
          <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
            items={tabItems}
          />
        </Card>
      </div>

      <div style={{ 
        position: 'fixed', 
        bottom: 0, 
        width: '100%', 
        maxWidth: 500, 
        background: '#fff',
        borderTop: '1px solid #f0f0f0'
      }}>
        <Tabs
          centered
          activeKey="service"
          onChange={key => {
            if (key !== 'service') {
              navigate(-1);
            }
          }}
          items={[
            {
              key: '1',
              label: (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <HomeOutlined />
                  <span>预定</span>
                </div>
              )
            },
            {
              key: '2',
              label: (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <IdcardOutlined />
                  <span>会员</span>
                </div>
              )
            },
            {
              key: 'service',
              label: (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <CustomerServiceOutlined />
                  <span>服务</span>
                </div>
              )
            },
            {
              key: '3',
              label: (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <UserOutlined />
                  <span>我的</span>
                </div>
              )
            }
          ]}
        />
      </div>

      {/* 评价弹窗 */}
      <Modal
        title="服务评价"
        open={rateModalVisible}
        onCancel={() => setRateModalVisible(false)}
        onOk={() => {
          message.success('感谢您的评价！');
          setRateModalVisible(false);
        }}
        okText="提交评价"
        cancelText="取消"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div style={{ textAlign: 'center' }}>
            <Rate 
              value={rating} 
              onChange={setRating} 
              style={{ fontSize: 24 }}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                {rating === 5 ? '非常满意' :
                 rating === 4 ? '满意' :
                 rating === 3 ? '一般' :
                 rating === 2 ? '不满意' :
                 '非常不满意'}
              </Text>
            </div>
          </div>
          <textarea
            style={{
              width: '100%',
              height: 100,
              padding: 8,
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              resize: 'none'
            }}
            placeholder="请输入您的评价内容（选填）"
            value={rateComment}
            onChange={(e) => setRateComment(e.target.value)}
          />
        </Space>
      </Modal>
    </div>
  );
};

export default HotelServicePage; 